import React, { useCallback, useEffect, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Divider, Modal, Progress, Row, Space, Spin, Tag, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Company } from 'src/features/companies/companies.types';
import { CheckCircleTwoTone, DeleteOutlined, InboxOutlined, ShopTwoTone } from '@ant-design/icons';
import { fetchCompany } from 'src/features/companies/companies.state';
import { useCompanyById } from 'src/features/companies/companies.hooks';
import {
  activatePackage,
  deletePackage,
  fetchPackage,
  updatePackage,
} from 'src/features/companyPackages/companyPackages.state';
import { usePackageById, usePackageImage } from 'src/features/companyPackages/companyPackages.hooks';
import { CompanyPackage, DraftCompanyPackage } from 'src/features/companyPackages/companyPackages.types';
import { AppDispatch } from 'src/index';
import MainLayout from 'src/components/layouts/MainLayout';
import SwitchSelectField from 'src/components/fields/SwitchSelectField';
import InputNumberEditableField from 'src/components/fields/InputNumberEditableField';
import { formatDatetime } from 'src/helpers/formatters';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';

const CompanyPackagePage: React.FC = () => {
  const { packageId, companyId } = useParams() as {
    packageId: string;
    companyId: string;
  };
  const dispatch: AppDispatch = useDispatch();
  const pkg = usePackageById(packageId) as CompanyPackage;
  const company = useCompanyById(companyId) as Company;
  const [, { progress, updatePackageImage }] = usePackageImage({
    packageId,
    companyId,
  });

  const [showImageUpload, setShowImageUpload] = useState<boolean>(false);
  const [showImage, setShowImage] = useState<boolean>(false);

  const handlePackageUpdate = useCallback(
    (data: Partial<DraftCompanyPackage>) => {
      dispatch(updatePackage({ companyId, packageId, data }));
    },
    [companyId, packageId, dispatch]
  );

  const handleDeleteButtonClick = useCallback(() => {
    dispatch(deletePackage({ packageId, companyId }));
  }, [companyId, dispatch, packageId]);

  const handlePackageActivation = useCallback(() => {
    dispatch(activatePackage({ packageId, companyId }));
  }, [companyId, dispatch, packageId]);

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/commercial/companies'>
                <Space>
                  <ShopTwoTone />
                  Компании
                </Space>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/commercial/companies/${companyId}`}>{company.name}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Space>
                <InboxOutlined />
                Пакеты
              </Space>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{pkg.name}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            {pkg.name}
          </Typography.Title>
          <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
            <div>
              <Typography.Title level={4}>Обложка пакета</Typography.Title>
              <div>
                {showImageUpload && (
                  <Row>
                    <input type='file' onChange={e => updatePackageImage((e.target.files as unknown as File[])[0])} />
                    <Progress percent={progress} size='small' width={200} />
                  </Row>
                )}
                {!showImageUpload && (
                  <Space>
                    <Button onClick={() => setShowImageUpload(true)}>Загрузить новую</Button>
                    {pkg.image && (
                      <>
                        <Button type='primary' onClick={() => setShowImage(true)}>
                          Открыть
                        </Button>
                        <Modal
                          visible={showImage}
                          title={`Обложка пакета ${pkg.name}`}
                          footer={null}
                          onCancel={() => setShowImage(false)}
                        >
                          <img alt='example' style={{ width: '100%' }} src={pkg.image} />
                        </Modal>
                      </>
                    )}
                  </Space>
                )}
              </div>
            </div>
          </ComponentRoleProtected>
        </div>
        <div className={styles.sidebar}>
          <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
            {!pkg.active && !pkg.archived && (
              <Button type='primary' onClick={handlePackageActivation}>
                Активировать
              </Button>
            )}
          </ComponentRoleProtected>
          {pkg.active && !pkg.archived && (
            <Tag color='green'>
              <Space>
                <CheckCircleTwoTone />
                Пакет активирован
              </Space>
            </Tag>
          )}
          {pkg.archived && <Tag color='red'>Пакет архивирован</Tag>}
          <div className={styles.propertiesGrid}>
            <Typography.Text className={styles.title}>Продолжительность</Typography.Text>
            <InputNumberEditableField value={pkg.duration} onBlur={e => handlePackageUpdate({ duration: e })} />
            <Typography.Text className={styles.title}>Цена</Typography.Text>
            <InputNumberEditableField value={pkg.price} onBlur={e => handlePackageUpdate({ price: e })} />
            <Typography.Text className={styles.title}>Доступно для VIP</Typography.Text>
            <SwitchSelectField value={pkg.vipAccess} onSelect={e => handlePackageUpdate({ vipAccess: e })} />
            <Typography.Text className={styles.title}>Promo пакет</Typography.Text>
            <SwitchSelectField value={pkg.promo} onSelect={e => handlePackageUpdate({ promo: e })} />
            <Typography.Text className={styles.title}>Детский</Typography.Text>
            <SwitchSelectField value={pkg.isKids} onSelect={e => handlePackageUpdate({ isKids: e })} />
          </div>
          <Divider />
          <Space direction='vertical' size='small'>
            <Typography.Text className={styles.footerDateStamp}>
              Создано: {formatDatetime(pkg.createdAt)}
            </Typography.Text>
            <Typography.Text className={styles.footerDateStamp}>
              Обновлено: {formatDatetime(pkg.updatedAt)}
            </Typography.Text>
            <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
              {!pkg.archived && (
                <Button type='link' onClick={handleDeleteButtonClick} danger size='small' style={{ padding: 0 }}>
                  <DeleteOutlined />
                  {pkg.active ? 'Архивировать' : 'Удалить'} пакет
                </Button>
              )}
            </ComponentRoleProtected>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const CompanyPackagePageBlock: React.FC = () => {
  const { packageId, companyId } = useParams() as {
    packageId: string;
    companyId: string;
  };
  const dispatch = useDispatch();
  const pkg = usePackageById(packageId);
  const company = useCompanyById(companyId);

  useEffect(() => {
    dispatch(fetchCompany({ companyId }));
    dispatch(fetchPackage({ packageId, companyId }));
  }, [companyId, dispatch, packageId]);

  if (!pkg || !company) {
    return (
      <Row align={'middle'} justify={'center'} style={{ minHeight: '100vh' }}>
        <Spin size={'large'} />
      </Row>
    );
  }

  return <CompanyPackagePage />;
};

export default CompanyPackagePageBlock;
